import type { IDBInspectionResult } from '@/types/accessor/api/emergencyInspectionResult/emergencyInspectionResult'
import { buildInspectionResultUpsertParam } from '@/stores/helpers/inspectionResultUpsertStore'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { useNetworkStore } from '@/stores/networkStore'
import { accessDataBasedOnNetwork } from '@/accessors/common/dataAccessorBasedOnNetwork'
import type { JsonData, UpdateData } from '../utils/interface'
import type { PhotoState } from '@/types/accessor/indexedDB'
import photoDBOperator from '@/accessors/indexedDB/photoDBOperator'

/**
 * zipファイルから取得したJsonDataをサーバーにアップロードする
 * @param uploadDataList zipファイルから取得したJsonData
 * @returns アップロード時にエラーになったデータ
 */
export const uploadJsonData = async (uploadDataList: JsonData[]): Promise<UpdateData[]> => {
  // 取得したデータをアップロードする
  const uploadJsonTasks = uploadDataList.map(async uploadData => {
    // 失敗したデータはzipファイルに保存するため、catchした場合はuploadDataを返す
    return await Promise.all([
      executeInspectionDataUpload(uploadData.inspectionResult),
      uploadPhotos(uploadData.photoStates)
    ])
      .then(() => {
        // 成功時は何も返さない
        return null
      })
      .catch(() => {
        // 失敗時、エラーデータとして返す
        const errorData: UpdateData = { ...uploadData, sendState: 'uploadFailed' }
        return errorData
      })
  })
  const errorData = await Promise.allSettled(uploadJsonTasks).then(results => {
    return results
      .map(result => {
        // 内部のPromiseがcatchでエラーをつぶしてerrorDataを返却しているため、fulfilledになる
        if (result.status !== 'fulfilled') return undefined
        // nullが返却された場合は成功、UpdateDataが返却された場合は失敗(errorData)としている
        if (result.value != null) return result.value
        // fulfilledにならない場合はPromise.allの部分でcatchしているため存在しないが、以下の記述がないとeslintに怒られるため必要
        return undefined
        // undefinedが存在しないよう型チェック
      })
      .filter((v): v is UpdateData => v !== undefined)
  })
  return errorData
}

/**
 * 調査実施結果APIを実行する
 * @param inspectionResult 調査データ
 * @returns なし
 */
export const executeInspectionDataUpload = async (
  inspectionResult: IDBInspectionResult
): Promise<void> => {
  try {
    // 調査実施結果APIのupsert用パラメータ生成
    const param = buildInspectionResultUpsertParam<
      typeof inspectionResult.assessmentInfo.constructionType
    >({
      assessmentDetail: inspectionResult.assessmentDetail,
      assessmentId: inspectionResult.assessmentId,
      emergencyAssessment: inspectionResult.emergencyAssessment,
      assessmentInfo: inspectionResult.assessmentInfo,
      building: inspectionResult.building,
      buildingOverview: inspectionResult.buildingOverview
    })
    if (param == null) {
      throw new GlobalError([ERROR_CODES.RESULT_PROPERTY_INVALID])
    }

    // 調査実施結果API呼び出し
    const networkStore = useNetworkStore()
    await accessDataBasedOnNetwork(
      'upsertInspectionResultApi',
      param,
      networkStore.getIsOnline,
      () => {
        networkStore.setOfflineStatus()
      }
    )
  } catch (e) {
    console.error(`assessmentId:${inspectionResult.assessmentId}のアップロード処理に失敗しました`)
    throw e
  }
}

/**
 * photoStateをIDBにアップロードする
 * @param photoStates 調査写真及びその情報が格納されたオブジェクト(indexedDBのものと同様)
 * @returns なし
 */
const uploadPhotos = async (photoStates: PhotoState[]): Promise<void> => {
  const uploadTasks = photoStates.map(async photoState => {
    if (photoState.arrayBuffer === undefined) return
    await photoDBOperator.upsertPhoto(photoState)
  })
  await Promise.all(uploadTasks)
}
